import { useTranslation } from 'react-i18next';
import type { ApiClientError } from '../lib/api.js';
import { ApiError } from './ApiError.js';

/**
 * The failure of a write, rendered the right way for its kind.
 *
 *  - a `conflict` whose `details.reason` has a translation gets that sentence
 *    *instead of* the generic one. The generic conflict text says "try again",
 *    which is right for a race and wrong for a duplicate or a unit that still
 *    has soldiers in it — nothing about retrying will change those.
 *  - anything else gets the translated code and the request id.
 *
 * Adding a reason on the server means adding `conflicts.<reason>` in both
 * catalogues; an untranslated reason falls back to the generic line, so a
 * missing string degrades rather than breaks.
 */
export function ActionError({ error }: { error: ApiClientError | null }) {
  const { t, i18n } = useTranslation();
  if (!error) return null;

  const reason = error.code === 'conflict' ? error.details.reason : undefined;
  if (typeof reason === 'string' && i18n.exists(`conflicts.${reason}`)) {
    return (
      <p role="alert" data-testid="api-error" data-reason={reason} className="text-sm text-red-600">
        {t(`conflicts.${reason}`)}
        {error.requestId && (
          <span className="latin-inline ms-2 text-slate-400">{error.requestId}</span>
        )}
      </p>
    );
  }

  return <ApiError code={error.code} requestId={error.requestId} />;
}
